import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Plus, Trash2, Calculator, FileText, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const INPUT = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text";
const CELL = "w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text";
const LABEL = "mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400";
const STATUSES = ["draft", "active", "converted", "cancelled"];
const idr = (v) => "IDR " + Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
const emptyLine = () => ({ category: "accommodation", description: "", supplier_ref: "", quantity: 1, unit: "unit", unit_cost: 0, currency: "IDR", fx_rate: 1, notes: "" });

export default function InquiryDetail() {
  const { inquiryId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEditInq = hasPermission("inquiries.edit");
  const canEditCost = hasPermission("costings.edit");
  const [inq, setInq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [qModal, setQModal] = useState(false);
  const canCreateQuote = hasPermission("quotations.create");

  const loadQuotes = async () => {
    try { setQuotes((await api.get("/quotations", { params: { inquiry_id: inquiryId } })).data); } catch { /* optional */ }
  };
  useEffect(() => { loadQuotes(); }, [inquiryId]); // eslint-disable-line

  const load = async () => {
    try { setInq((await api.get(`/inquiries/${inquiryId}`)).data); }
    catch (e) { toast.error(apiError(e)); navigate("/inquiries"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [inquiryId]); // eslint-disable-line

  const saveInquiry = async () => {
    try {
      const p = { status: inq.status, origin: inq.origin, trip_type: inq.trip_type, num_travelers: Number(inq.num_travelers) || 1, notes: inq.notes, requirements: inq.requirements || {} };
      const r = await api.patch(`/inquiries/${inquiryId}`, p);
      setInq((s) => ({ ...s, ...r.data }));
      toast.success("Inquiry saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600 dark:text-ink-gold" /></div>;
  if (!inq) return null;
  const req = inq.requirements || {};
  const setReq = (k) => (e) => setInq((s) => ({ ...s, requirements: { ...(s.requirements || {}), [k]: e.target.value } }));

  return (
    <div className="max-w-5xl space-y-6" data-testid="inquiry-detail-page">
      <button onClick={() => navigate("/inquiries")} data-testid="back-to-inquiries" className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-sky-600 dark:text-slate-400 dark:hover:text-ink-gold">
        <ArrowLeft className="h-4 w-4" /> Back to Inquiries
      </button>

      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="font-mono-tf text-xs font-semibold text-slate-500 dark:text-ink-gold" data-testid="inquiry-detail-number">{inq.inquiry_number}</span>
          <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">{inq.customer_name || "Inquiry"}</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{(inq.destinations || []).join(", ") || "No destinations"} · {inq.num_travelers} pax</p>
        </div>
        {canCreateQuote && (
          <button onClick={() => setQModal(true)} data-testid="create-quotation-button" className="flex shrink-0 items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep">
            <FileText className="h-4 w-4" /> Create Quotation
          </button>
        )}
      </div>

      {/* Trip requirements */}
      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display font-semibold text-slate-900 dark:text-ink-text">Trip Requirements</h2>
          {canEditInq && <button onClick={saveInquiry} data-testid="save-inquiry-button" className="flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-sky-500 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep"><Save className="h-4 w-4" /> Save</button>}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div><label className={LABEL}>Status</label><select disabled={!canEditInq} value={inq.status} onChange={(e) => setInq((s) => ({ ...s, status: e.target.value }))} data-testid="inquiry-status-select" className={INPUT}>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
          <div><label className={LABEL}>Origin</label><input disabled={!canEditInq} value={inq.origin || ""} onChange={(e) => setInq((s) => ({ ...s, origin: e.target.value }))} className={INPUT} /></div>
          <div><label className={LABEL}>Trip Type</label><input disabled={!canEditInq} value={inq.trip_type || ""} onChange={(e) => setInq((s) => ({ ...s, trip_type: e.target.value }))} className={INPUT} /></div>
          {[["accommodation","Accommodation"],["transportation","Transportation"],["activities","Activities / Experiences"],["meals","Meals"],["guide_driver","Guide / Driver"],["other_services","Other Services"]].map(([k,l]) => (
            <div key={k}><label className={LABEL}>{l}</label><input disabled={!canEditInq} value={req[k] || ""} onChange={setReq(k)} data-testid={`req-${k}`} className={INPUT} /></div>
          ))}
        </div>
        <div className="mt-4"><label className={LABEL}>Special Notes</label><textarea disabled={!canEditInq} rows={2} value={req.special_notes || ""} onChange={setReq("special_notes")} className={INPUT} /></div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid="inquiry-quotations">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display font-semibold text-slate-900 dark:text-ink-text">Quotations</h2>
        </div>
        {quotes.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="inquiry-quotations-empty">No quotations yet. Save a costing, then create a quotation.</p>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-ink-line">
            {quotes.map((qt) => (
              <button key={qt.id} onClick={() => navigate(`/quotations/${qt.id}`)} data-testid={`inquiry-quotation-${qt.quotation_number}`}
                className="flex w-full items-center justify-between gap-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-ink-panel/40">
                <span className="font-mono-tf text-xs font-semibold text-slate-700 dark:text-ink-gold">{qt.revision_label}</span>
                <span className="font-mono-tf text-slate-600 dark:text-slate-300">{qt.currency} {Number(qt.customer_total || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}</span>
                <span className="text-xs uppercase tracking-wider text-slate-400" data-testid={`inquiry-quotation-status-${qt.quotation_number}`}>{qt.display_status}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {hasPermission("costings.view") && <CostingWorkspace inquiryId={inquiryId} canEdit={canEditCost} />}
      {qModal && <QuotationModal inquiryId={inquiryId} onClose={() => setQModal(false)} onSaved={(id) => navigate(`/quotations/${id}`)} />}
    </div>
  );
}

function QuotationModal({ inquiryId, onClose, onSaved }) {
  const [form, setForm] = useState({ currency: "IDR", fx_rate: "1", valid_until: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const isIdr = (form.currency || "IDR").toUpperCase() === "IDR";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { inquiry_id: inquiryId, currency: form.currency.toUpperCase(), fx_rate: isIdr ? 1 : Number(form.fx_rate) || 0 };
      if (form.valid_until) payload.valid_until = form.valid_until;
      if (form.notes.trim()) payload.notes = form.notes.trim();
      const r = await api.post("/quotations", payload);
      toast.success(`Quotation ${r.data.revision_label} created`);
      onSaved(r.data.id);
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="quotation-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-ink-line">
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-ink-text">New Quotation</h3>
          <button onClick={onClose} data-testid="quotation-modal-close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4 px-6 py-5">
          <p className="text-xs text-slate-400">Built from the current saved costing. The IDR base stays internal; the customer sees only the total below in their currency.</p>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={LABEL}>Currency (ISO)</label><input value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))} maxLength={3} data-testid="quotation-currency-input" className={`${INPUT} font-mono-tf uppercase`} /></div>
            <div><label className={LABEL}>FX rate (per 1 unit)</label><input disabled={isIdr} type="number" value={isIdr ? 1 : form.fx_rate} onChange={(e) => setForm((f) => ({ ...f, fx_rate: e.target.value }))} data-testid="quotation-fx-input" className={`${INPUT} disabled:opacity-40`} /></div>
          </div>
          <div><label className={LABEL}>Valid until</label><input type="date" value={form.valid_until} onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))} data-testid="quotation-valid-until-input" className={INPUT} /></div>
          <div><label className={LABEL}>Customer notes</label><textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="quotation-notes-input" className={INPUT} /></div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300">Cancel</button>
            <button type="submit" disabled={saving} data-testid="quotation-modal-save" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-60 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CostingWorkspace({ inquiryId, canEdit }) {
  const [lines, setLines] = useState([]);
  const [markupPct, setMarkupPct] = useState(0);
  const [markupFixed, setMarkupFixed] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/inquiries/${inquiryId}/costing`);
      setLines((r.data.lines || []).map((l) => ({ ...emptyLine(), ...l, currency: l.source_currency || l.currency || "IDR" })));
      setMarkupPct(r.data.markup_percent || 0);
      setMarkupFixed(r.data.markup_fixed || 0);
      setSummary(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [inquiryId]); // eslint-disable-line

  const setLine = (idx, k, v) => setLines((ls) => ls.map((l, i) => i === idx ? { ...l, [k]: v } : l));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        markup_percent: Number(markupPct) || 0, markup_fixed: Number(markupFixed) || 0,
        lines: lines.map((l) => ({ category: l.category, description: l.description, supplier_ref: l.supplier_ref || null, quantity: Number(l.quantity) || 0, unit: l.unit, unit_cost: Number(l.unit_cost) || 0, currency: (l.currency || "IDR").toUpperCase(), fx_rate: Number(l.fx_rate) || 1, notes: l.notes || null })),
      };
      const r = await api.put(`/inquiries/${inquiryId}/costing`, payload);
      setSummary(r.data);
      setLines((r.data.lines || []).map((l) => ({ ...emptyLine(), ...l, currency: l.source_currency || l.currency || "IDR" })));
      toast.success(`Costing saved (v${r.data.version})`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-sky-600 dark:text-ink-gold" /></div>;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid="costing-workspace">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2"><Calculator className="h-4 w-4 text-sky-600 dark:text-ink-gold" /><h2 className="font-display font-semibold text-slate-900 dark:text-ink-text">Costing {summary?.version ? `(v${summary.version})` : ""}</h2></div>
        {canEdit && <button onClick={addLine} data-testid="add-cost-line-button" className="flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300 dark:hover:bg-ink-panel"><Plus className="h-4 w-4" /> Add line</button>}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="cost-lines-table">
          <thead>
            <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-500 dark:border-ink-line">
              <th className="px-2 py-2">Category</th><th className="px-2 py-2">Description</th><th className="px-2 py-2">Supplier</th>
              <th className="px-2 py-2">Qty</th><th className="px-2 py-2">Unit cost</th><th className="px-2 py-2">Cur</th>
              <th className="px-2 py-2">FX→IDR</th><th className="px-2 py-2 text-right">IDR amount</th><th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && <tr><td colSpan={9} className="px-2 py-6 text-center text-sm text-slate-400" data-testid="costing-empty">No cost lines. Add one to begin.</td></tr>}
            {lines.map((l, idx) => {
              const foreign = (l.currency || "IDR").toUpperCase() !== "IDR";
              const idrAmt = (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0) * (foreign ? (Number(l.fx_rate) || 0) : 1);
              return (
                <tr key={idx} className="border-b border-slate-100 dark:border-ink-line" data-testid={`cost-line-${idx}`}>
                  <td className="px-2 py-1.5"><input disabled={!canEdit} value={l.category} onChange={(e) => setLine(idx, "category", e.target.value)} data-testid={`cost-line-${idx}-category`} className={CELL} /></td>
                  <td className="px-2 py-1.5"><input disabled={!canEdit} value={l.description} onChange={(e) => setLine(idx, "description", e.target.value)} data-testid={`cost-line-${idx}-description`} className={CELL} /></td>
                  <td className="px-2 py-1.5"><input disabled={!canEdit} value={l.supplier_ref || ""} onChange={(e) => setLine(idx, "supplier_ref", e.target.value)} className={CELL} /></td>
                  <td className="px-2 py-1.5 w-16"><input disabled={!canEdit} type="number" value={l.quantity} onChange={(e) => setLine(idx, "quantity", e.target.value)} data-testid={`cost-line-${idx}-qty`} className={CELL} /></td>
                  <td className="px-2 py-1.5 w-28"><input disabled={!canEdit} type="number" value={l.unit_cost} onChange={(e) => setLine(idx, "unit_cost", e.target.value)} data-testid={`cost-line-${idx}-unit-cost`} className={CELL} /></td>
                  <td className="px-2 py-1.5 w-20"><input disabled={!canEdit} value={l.currency} onChange={(e) => setLine(idx, "currency", e.target.value.toUpperCase())} data-testid={`cost-line-${idx}-currency`} className={`${CELL} font-mono-tf uppercase`} /></td>
                  <td className="px-2 py-1.5 w-24"><input disabled={!canEdit || !foreign} type="number" value={l.fx_rate} onChange={(e) => setLine(idx, "fx_rate", e.target.value)} data-testid={`cost-line-${idx}-fx`} className={`${CELL} disabled:opacity-40`} /></td>
                  <td className="px-2 py-1.5 text-right font-mono-tf text-slate-700 dark:text-slate-200" data-testid={`cost-line-${idx}-idr`}>{idr(idrAmt)}</td>
                  <td className="px-2 py-1.5">{canEdit && <button onClick={() => removeLine(idx)} data-testid={`remove-cost-line-${idx}`} className="rounded p-1 text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="space-y-3">
          <div><label className={LABEL}>Markup %</label><input disabled={!canEdit} type="number" value={markupPct} onChange={(e) => setMarkupPct(e.target.value)} data-testid="markup-percent-input" className={INPUT} /></div>
          <div><label className={LABEL}>Markup fixed (IDR)</label><input disabled={!canEdit} type="number" value={markupFixed} onChange={(e) => setMarkupFixed(e.target.value)} data-testid="markup-fixed-input" className={INPUT} /></div>
          {canEdit && <button onClick={save} disabled={saving} data-testid="save-costing-button" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-60 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />} Calculate & Save</button>}
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-ink-line dark:bg-ink-panel/40" data-testid="costing-summary">
          {[["Total cost", summary?.total_cost, "total-cost"], ["Markup", summary?.markup_amount, "markup-amount"], ["Selling price", summary?.selling_price, "selling-price"], ["Gross profit", summary?.gross_profit, "gross-profit"]].map(([l, v, tid]) => (
            <div key={tid} className="flex items-center justify-between py-1.5 text-sm">
              <span className="text-slate-500 dark:text-slate-400">{l}</span>
              <span className="font-mono-tf font-semibold text-slate-800 dark:text-ink-text" data-testid={`summary-${tid}`}>{idr(v)}</span>
            </div>
          ))}
          <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2 dark:border-ink-line">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Gross margin</span>
            <span className="font-mono-tf text-lg font-bold text-emerald-600 dark:text-emerald-400" data-testid="summary-gross-margin">{Number(summary?.gross_margin_percent || 0).toFixed(2)}%</span>
          </div>
        </div>
      </div>
    </section>
  );
}
